import * as React from 'react';
import { BaseModal, BaseModalProps } from '@tcl-boron-foundations/base-modal';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { Button } from '@tcl-boron-prefabs/button';
import { useModalStyles } from './styles';

export interface ModalButtonProps {
  id: string;
  buttonText: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}
export interface ModalProps extends Omit<BaseModalProps, 'contentLabel'> {
  /** The descriptive title of this modal. */
  title: string;
  /** Tempus modals come in 3 standard widths, represented here as 'small', 'medium', and 'large'. */
  size: 'small' | 'medium' | 'large' | 'fullScreen';
  /**
   * Button to handle primary action/call to action in modal.
   * Button type is forced to be primary.
   * Examples include "Confirm" or "Upload"
   * If no button is provided, the button will not be shown.
   */
  callToActionButton?: ModalButtonProps;
  /**
   * Button to handle cancel action in modal.
   * Button type is forced to be tertiary.
   * If no button is provided, the button will not be shown.
   */
  cancelButton?: ModalButtonProps;
  /**
   * Button to handle secondary action in modal.
   * Button type is forced to be secondary.
   * If no button is provided, the button will not be shown.
   */
  secondaryActionButton?: ModalButtonProps;
  /**boolean that indicates if the content in the modal should be center justified.
   * Header, primary action button, and content will be center aligned */
  centerJustified?: boolean;
  /**An illustration that can be passed to the modal that will display at the top of the modal with the header below it.
   * All content will be center justified.
   */
  illustration?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  children,
  classes: classOverrides = {},
  className,
  callToActionButton,
  secondaryActionButton,
  cancelButton,
  size,
  title,
  centerJustified = false,
  illustration,
  ...props
}) => {
  const classes = mergeClasses(
    useModalStyles({ size, title, isOpen: props.isOpen, centerJustified, illustration }),
    classOverrides,
    {
      root: className as string,
    },
  );

  return (
    <BaseModal contentLabel={title} classes={{ root: classes.root }} data-analytics="modal" {...props}>
      <div className={classes.titleContentWrapper}>
        {illustration ? (
          <div>
            <div className={classes.illustration}>{illustration}</div>
            <h1 className={classes.title}>{title}</h1>
          </div>
        ) : (
          <div>
            <h1 className={classes.title}>{title}</h1>
          </div>
        )}
        <div className={classes.content}>{children}</div>
      </div>
      <div className={classes.buttons}>
        {cancelButton && (
          <Button
            buttonType="tertiary"
            small={size === 'small' || size === 'medium' ? true : false}
            onClick={cancelButton.onClick}
            id={cancelButton.id}
            disabled={cancelButton.disabled ?? false}
            ariaLabel={cancelButton.buttonText}
            loading={cancelButton.loading}
          >
            {cancelButton.buttonText}
          </Button>
        )}
        {secondaryActionButton && (
          <Button
            buttonType="secondary"
            small={size === 'small' || size === 'medium' ? true : false}
            onClick={secondaryActionButton.onClick}
            id={secondaryActionButton.id}
            disabled={secondaryActionButton.disabled ?? false}
            ariaLabel={secondaryActionButton.buttonText}
            loading={secondaryActionButton.loading}
          >
            {secondaryActionButton.buttonText}
          </Button>
        )}
        {callToActionButton && (
          <Button
            buttonType="primary"
            small={size === 'small' || size === 'medium' ? true : false}
            onClick={callToActionButton.onClick}
            id={callToActionButton.id}
            disabled={callToActionButton.disabled ?? false}
            ariaLabel={callToActionButton.buttonText}
            loading={callToActionButton.loading}
          >
            {callToActionButton.buttonText}
          </Button>
        )}
      </div>
    </BaseModal>
  );
};
